import React, { useContext, useEffect } from "react";
import { NavLink, useHistory, useParams } from "polyrouter";
import { Layout } from "views/Main/Layouts";
import { useTranslation } from "utilities/i18n";
import { Card, Field, makeColumnContentForCard } from "collections/Cards";
import { Section } from "collections/Section";
import {
  EntitlementGrouping,
  useGetEntitlementGroupingQuery
} from "../useEntitlements";
import { handleNullFields } from "utilities/stringUtils";
import { CategoryTitle, Loader } from "elements";
import {
  toLicenseBeginsIntSecondsString,
  toLicenseDateLocaleDateString,
  toLicenseExpiryLocaleDateString,
  toLicenseUnlimitedCountString
} from "utilities/licenseServerUtils";
import { SelectedTenantContext } from "polyrouter/SelectedTenantProvider";

export const EntitlementGroupingInformation: React.FC<{
  entitlement: EntitlementGrouping;
}> = ({ entitlement }) => {
  const { Content } = Layout;
  const { t } = useTranslation();

  const fields: Field[] = [
    {
      key: "type",
      label: t("TYPE"),
      content: <p>{handleNullFields(entitlement.productName)}</p>
    },
    {
      key: "version",
      label: t("VERSION_ALT"),
      content: <p>{handleNullFields(entitlement.productVersion)}</p>
    },
    {
      key: "licenseType",
      label: t("LICENSE_TYPE"),
      content: <p>{handleNullFields(entitlement.typeName)}</p>
    },
    {
      key: "assignmentType",
      label: t("ENTITLEMENT_ASSIGNMENT_TYPE"),
      content: <p>{handleNullFields(entitlement.assignmentTypeName)}</p>
    },
    {
      key: "attributeCount",
      label: t("ATTRIBUTE_COUNT"),
      content: (
        <p>{toLicenseUnlimitedCountString(entitlement.attributeCount)}</p>
      )
    },
    {
      key: "attributeType",
      label: t("ATTRIBUTE_TYPE"),
      content: <p>{handleNullFields(entitlement.attributeTypeName)}</p>
    },
    {
      key: "assignedCount",
      label: t("ASSIGNED_ATTRIBUTES"),
      content: <p>{toLicenseUnlimitedCountString(entitlement.assignedCount)}</p>
    },
    {
      key: "assignedProductCount",
      label: t("ENTITLEMENT_ASSIGNED_PRODUCT_COUNT"),
      content: (
        <p>{toLicenseUnlimitedCountString(entitlement.assignedProductCount)}</p>
      )
    },
    {
      key: "pendingAssignmentCount",
      label: t("ENTITLEMENT_PENDING_ATTRIBUTES"),
      content: (
        <p>
          {toLicenseUnlimitedCountString(entitlement.pendingAssignmentCount)}
        </p>
      )
    },
    {
      key: "pendingAssignmentProductCount",
      label: t("ENTITLEMENT_PENDING_ASSIGN_PRODUCT_COUNT"),
      content: (
        <p>
          {toLicenseUnlimitedCountString(
            entitlement.pendingAssignmentProductCount
          )}
        </p>
      )
    },
    {
      key: "pendingReleaseCount",
      label: t("ENTITLEMENT_PENDING_RELEASE_ATTRIBUTES"),
      content: (
        <p>{toLicenseUnlimitedCountString(entitlement.pendingReleaseCount)}</p>
      )
    },
    {
      key: "pendingReleaseProductCount",
      label: t("ENTITLEMENT_PENDING_RELEASE_PRODUCT_COUNT"),
      content: (
        <p>
          {toLicenseUnlimitedCountString(
            entitlement.pendingReleaseProductCount
          )}
        </p>
      )
    },
    {
      key: "removedCount",
      label: t("REMOVED_ATTRIBUTES"),
      content: <p>{toLicenseUnlimitedCountString(entitlement.removedCount)}</p>
    },
    {
      key: "availableCount",
      label: t("AVAILABLE_ATTRIBUTES"),
      content: (
        <p>{toLicenseUnlimitedCountString(entitlement.availableCount)}</p>
      )
    },
    {
      key: "creationTime",
      label: t("CREATED"),
      content: <p>{toLicenseDateLocaleDateString(entitlement.creationTime)}</p>
    },
    {
      key: "lastModifiedTime",
      label: t("LAST_MODIFIED"),
      content: (
        <p>{toLicenseDateLocaleDateString(entitlement.lastModifiedTime)}</p>
      )
    },
    {
      key: "startDate",
      label: t("START_DATE"),
      content: <p>{toLicenseBeginsIntSecondsString(entitlement.begins)}</p>
    },
    {
      key: "expires",
      label: t("EXPIRES"),
      content: <p>{toLicenseExpiryLocaleDateString(entitlement.expires)}</p>
    },
    {
      key: "activationCode",
      label: t("ACTIVATION_KEY"),
      content: <p>{handleNullFields(entitlement.activationCode)}</p>
    },
    {
      key: "mappingId1",
      label: t("SKU"),
      content: <p>{handleNullFields(entitlement.mappingId1)}</p>
    },
    {
      key: "mappingId2",
      label: t("ORDER_NUMBER"),
      content: <p>{handleNullFields(entitlement.mappingId2)}</p>
    }
  ];
  return (
    <>
      <Content.Title>
        <CategoryTitle
          title={`${entitlement.productName ?? ""} ${t("ENTITLEMENT")}`}
          subtitle={entitlement.name}
        />
      </Content.Title>
      <Section>
        <Section.Header>{t("ENTITLEMENT_INFORMATION")}</Section.Header>
        <Card className="table-card full-width">
          {makeColumnContentForCard(fields, 4)}
        </Card>
      </Section>
    </>
  );
};

export const EntitlementGroupingDetails: React.FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { tenant } = useContext(SelectedTenantContext);
  const tenantId = tenant?.id ?? "";

  const { data, fetching } = useGetEntitlementGroupingQuery({
    tenantId: tenantId,
    groupingId: id
  });

  const entitlement = data?.entitlement;

  useEffect(() => {
    if (!fetching && tenantId) {
      if (
        !entitlement ||
        id !== entitlement.groupingId ||
        tenantId !== entitlement.tenantId
      ) {
        history.push("/manage/licensing/clariti/entitlements");
      }
    }
  }, [fetching, tenantId, id, entitlement, history]);

  const { Sidebar, Content } = Layout;

  return (
    <>
      <Sidebar>
        <span className="title-capitalized">{t("CLARITI")}</span>
        <ul className="menu router">
          <li>
            <NavLink nest exact to={""}>
              {t("OVERVIEW")}
            </NavLink>
          </li>
        </ul>
      </Sidebar>
      <Content
        className="product-catalog-details"
        data-testid="product-catalog-details"
      >
        <Loader loading={fetching}>
          {entitlement && (
            <EntitlementGroupingInformation entitlement={entitlement} />
          )}
        </Loader>
      </Content>
    </>
  );
};
